import SearchPanel from '@Components/Search/SearchPanel'
import PartyResultsTable from '@Components/Search/SearchByParty/PartyResultsTable'
import PartyResultsCardTable from '@Components/Search/SearchByParty/PartyResultsCardTable'
import usePartySearchQuery from '@Components/Search/SearchByParty/hooks/usePartySearchQuery'
import usePartyWorksQuery from '@Components/Search/SearchByParty/hooks/usePartyWorksQuery'
import { QueryPartyStore } from '@App/stores/search/party/query-party-store'
import React, { JSX, useEffect, useRef, useState } from 'react'
import { HasSearchCriteria } from '@App/stores/search/helpers/has-search-criteria'
import { isHasSearchFilter } from '@App/stores/search/helpers/has-search-filter'
import { observer } from 'mobx-react'
import PointPreloader from '@Components/Preloaders/PointPreloader'
import { SearchPartyStore } from '@App/stores/search/party/search-party-store'
import PartyWorksTable from '@Components/Search/SearchByParty/PartyWorksTable'
import AnimatedDualSearchTables from '@Components/Search/SearchByParty/AnimatedDualSearchTables'
import CatalogExtendedWorkItem from '@Components/WorkItemV2/Catalog/CatalogExtendedWorkItem'
import useFilterUrlSync from '@Components/Search/SearchPanel/useFilterUrlSync'
import useSearchCriteriaUrlSync from '@Components/Search/SearchPanel/useSearchCriteriaUrlSync'
import { mapCriteriaToObject } from '@App/helpers/map-criteria-to-object'

interface SearchByPartyProps<resultType, T extends Record<string, never>, Q extends object> {
    searchPartyStore: SearchPartyStore<resultType> & HasSearchCriteria<T>
    renderPartyResultsTable?: (
        onTitleClick: (partyDetails: resultType) => Promise<void>,
        setLoading: (state: boolean) => void
    ) => JSX.Element
    partyDetailsCardComponent: React.ComponentType<{
        partyDetails: resultType
        onTitleClick: (partyDetails: resultType) => void
    }>
    /**
     * Opt in to running the primary search through TanStack Query, like the other public searches: pass the same
     * store again. Inherited members would make this look available on stores that can't do it, so it isn't inferred.
     */
    partyQueryStore?: QueryPartyStore<resultType, Q>
    /** Carry the submitted search terms in the URL so the search is linkable. Off for pages embedded in a flow. */
    syncSearchTermsToUrl?: boolean
}

const SearchByParty = <
    resultType,
    T extends Record<string, never>,
    K extends Record<string, any>,
    Q extends object = object,
>({
    searchPartyStore,
    renderPartyResultsTable,
    partyDetailsCardComponent,
    partyQueryStore,
    syncSearchTermsToUrl = false,
}: SearchByPartyProps<resultType, T, Q>) => {
    const [searching, setLoading] = useState<boolean>(false)

    // A link's search terms must be in the criteria store before the form first renders (it picks its rows up
    // then), hence a state initializer. The search itself runs once after mount, only if the terms are complete.
    const { urlCriteria, syncCriteriaToUrl } = useSearchCriteriaUrlSync(
        searchPartyStore.searchCriteriaStore.criteriaOptions
    )
    const [linkedQuery] = useState<Partial<T> | undefined>(() => {
        if (!syncSearchTermsToUrl || !urlCriteria) {
            return undefined
        }
        searchPartyStore.resetStore()
        const criteriaStore = searchPartyStore.searchCriteriaStore
        criteriaStore.applyCriteriaFromObject(urlCriteria)
        const rows = criteriaStore.searchCriteria
        const linked = criteriaStore.isCriteriaSearchReady(rows) ? (mapCriteriaToObject(rows) as Partial<T>) : undefined
        // A query-driven store searches as soon as it has a query, so the linked terms only need to be set.
        if (linked && partyQueryStore) {
            searchPartyStore.setQuery?.(linked)
            return undefined
        }
        return linked
    })

    // After the seeding above: the query key is read from the store, so the store must already hold a linked search.
    const partySearch = usePartySearchQuery(partyQueryStore)
    const partyWorks = usePartyWorksQuery(partyQueryStore)
    const loading = searching || partySearch.isFetching

    const handleSearch = async (submittedSearchCriteria: Partial<T>, filters: Partial<K>) => {
        if (syncSearchTermsToUrl) {
            syncCriteriaToUrl(submittedSearchCriteria)
        }
        if (partyQueryStore) {
            partySearch.search(submittedSearchCriteria as Q)
            return
        }
        setLoading(true)
        if (searchPartyStore.setQuery) {
            searchPartyStore.setQuery(submittedSearchCriteria)
        }
        if (isHasSearchFilter(searchPartyStore) && searchPartyStore.searchFilters.length > 0) {
            searchPartyStore.setSearchFilters(filters)
        }
        await searchPartyStore.search()
        setLoading(false)
    }

    const onTitleClick = async (partyDetails: resultType) => {
        if (partyQueryStore) {
            partyQueryStore.selectParty(partyDetails)
            return
        }
        await searchPartyStore.secondarySearch(partyDetails)
    }

    const reset = () => {
        searchPartyStore.resetStore()
    }

    const PartyResultTableRender = partyQueryStore ? (
        <PartyResultsCardTable
            partyQueryStore={partyQueryStore}
            loading={partySearch.isFetching}
            onTitleClick={onTitleClick}
            PartyDetailsCardComponent={partyDetailsCardComponent}
        />
    ) : renderPartyResultsTable ? (
        renderPartyResultsTable(onTitleClick, setLoading)
    ) : (
        <PartyResultsTable
            onTitleClick={onTitleClick}
            searchPartyStore={searchPartyStore}
            setLoading={setLoading}
            PartyDetailsCardComponent={partyDetailsCardComponent}
        />
    )

    const filters =
        isHasSearchFilter(searchPartyStore) && searchPartyStore.searchFilters.length > 0
            ? searchPartyStore.searchFilters
            : undefined

    // No auto-search runs on mount (public-search style, waits for the user's own submission), so
    // this only pre-populates the filter UI ready for that search.
    const { urlFilterValues, syncFiltersToUrl } = useFilterUrlSync<K>(filters)

    const hasRunLinkedSearch = useRef(false)
    useEffect(() => {
        if (!linkedQuery || hasRunLinkedSearch.current) {
            return
        }
        hasRunLinkedSearch.current = true
        void handleSearch(linkedQuery, (urlFilterValues ?? {}) as Partial<K>)
    }, [])

    return (
        <div className="search-by-party">
            <SearchPanel
                canAddCriteria={!!searchPartyStore.canAddCriteria}
                searchCriteriaStore={searchPartyStore.searchCriteriaStore}
                onSearch={handleSearch}
                onReset={() => {
                    syncFiltersToUrl(undefined)
                    if (syncSearchTermsToUrl) {
                        syncCriteriaToUrl(undefined)
                    }
                    reset()
                }}
                filterFieldCollections={filters}
                defaultFilterValues={urlFilterValues}
                onFilterValueChange={syncFiltersToUrl}
                disabled={loading}
            />
            {loading && !partyQueryStore ? (
                <PointPreloader />
            ) : (
                <AnimatedDualSearchTables
                    dualSearchStore={searchPartyStore}
                    primarySearchTable={PartyResultTableRender}
                    secondarySearchTable={
                        <PartyWorksTable
                            searchPartyStore={searchPartyStore}
                            extendedCardType={CatalogExtendedWorkItem}
                            className="public-search-results-table"
                            loading={partyQueryStore ? partyWorks.isFetching : undefined}
                            onChangePage={
                                partyQueryStore &&
                                ((pagination) => partyQueryStore.setSecondaryPage(pagination.page ?? 1))
                            }
                            onChangeLimit={
                                partyQueryStore && ((limit) => partyQueryStore.setSecondaryResultsPerPage(limit))
                            }
                        />
                    }
                />
            )}
        </div>
    )
}

export default observer(SearchByParty)
